// The ONE way the portal talks to the API. Same-origin (Vite proxies /api in
// dev; the Worker serves the portal in prod), cookie session, JSON in/out.
//
//   const data = await api<{ items: Thing[] }>("/api/things", { query: { igAccountId } });
//   await api("/api/things", { method: "POST", body: { name } });
//
// Errors throw ApiError { status, code, message } — message is safe to show
// in a toast. A 401 on any non-auth route sends the browser to /login?next=.

import type {
  AdminStats,
  CreateAccountReq,
  CreateAccountRes,
  ID,
  IgAccount,
  IgAccountOverview,
  LoginReq,
  LoginRes,
  Me,
  Page,
  Role,
  ResellerAccountsRes,
  SettingsMap,
  Task,
  TaskWithContact,
  UpdateAccountReq,
  User,
} from "@gf/shared";
import type { UsageAccount } from "@gf/shared/features/account-usage";
import { trackSuccessfulWorkflow } from './analytics';

export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable code from the API body ({ error, code }), or "network" / "http_<status>". */
  readonly code: string;
  constructor(status: number, code: string, message: string, readonly upgradeUrl?: string, readonly signupUrl?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function isApiError(err: unknown, status?: number): err is ApiError {
  return err instanceof ApiError && (status === undefined || err.status === status);
}

export type QueryValue = string | number | boolean | null | undefined;

export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Plain objects are sent as JSON; FormData / Blob are sent as-is. */
  body?: unknown;
  /** null / undefined / "" values are dropped. */
  query?: Record<string, QueryValue | QueryValue[]>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Don't bounce to /login on 401 (for calls that expect it). */
  noAuthRedirect?: boolean;
}

const FRIENDLY: Record<number, string> = {
  400: "That request didn't look right. Check the form and try again.",
  401: "Your session ended. Sign in again.",
  403: "You don't have access to that.",
  404: "We couldn't find that.",
  409: "That conflicts with something that already exists.",
  413: "That file is too big.",
  429: "Easy, tiger. Too many requests. Try again in a minute.",
};

const PUBLIC_PATHS = ["/login", "/forgot", "/set-password", "/reset", "/sso"];

function buildUrl(path: string, query?: ApiOptions["query"]): string {
  const p = path.startsWith("/api/") || path === "/api" ? path : `/api${path.startsWith("/") ? "" : "/"}${path}`;
  if (!query) return p;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    const values = Array.isArray(v) ? v : [v];
    for (const item of values) {
      if (item === null || item === undefined || item === "") continue;
      qs.append(k, String(item));
    }
  }
  const s = qs.toString();
  return s ? `${p}${p.includes("?") ? "&" : "?"}${s}` : p;
}

function redirectToLogin() {
  const { pathname, search } = window.location;
  if (PUBLIC_PATHS.some((x) => pathname === x || pathname.startsWith(`${x}/`))) return;
  const next = encodeURIComponent(pathname + search);
  window.location.assign(`/login?next=${next}`);
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = opts.body !== undefined ? "POST" : "GET", body, query, signal, headers = {}, noAuthRedirect } = opts;
  const url = buildUrl(path, query);
  const init: RequestInit = {
    method,
    signal,
    credentials: "include",
    headers: { Accept: "application/json", ...headers },
  };
  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob || typeof body === "string") {
      init.body = body as BodyInit;
    } else {
      init.body = JSON.stringify(body);
      (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    }
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "network", "Can't reach GramFather. Check your connection and try again.");
  }

  if (res.ok) {
    const type = res.headers.get("content-type") ?? "";
    const result = res.status === 204 ? undefined : type.includes("application/json") ? await res.json() : await res.text();
    try { trackSuccessfulWorkflow(method, url); } catch { /* Optional measurement never changes a successful API result. */ }
    return result as T;
  }

  let payload: { error?: string; code?: string; upgradeUrl?: string; signupUrl?: string } | null = null;
  try {
    const type = res.headers.get("content-type") ?? "";
    if (type.includes("application/json")) payload = (await res.json()) as { error?: string; code?: string };
  } catch {
    payload = null;
  }
  const code = payload?.code ?? `http_${res.status}`;
  const message =
    (payload?.error && payload.error.trim()) ||
    FRIENDLY[res.status] ||
    (res.status >= 500 ? "GramFather hit a snag on our end. Try again in a moment." : `Request failed (${res.status}).`);

  if (res.status === 401 && !noAuthRedirect && !url.startsWith("/api/auth/")) redirectToLogin();
  throw new ApiError(res.status, code, message, payload?.upgradeUrl, payload?.signupUrl);
}

// ── Typed endpoint helpers (core). Features add their own in their folder. ──

export const authApi = {
  me: () => api<{ user: Me }>("/api/auth/me", { noAuthRedirect: true }),
  login: (email: string, password: string) =>
    api<LoginRes>("/api/auth/login", { method: "POST", body: { email, password, client: "web" } satisfies LoginReq }),
  logout: () => api<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  forgot: (email: string) => api<{ ok: boolean }>("/api/auth/forgot", { method: "POST", body: { email } }),
  reset: (token: string, password: string) => api<{ ok: boolean }>("/api/auth/reset", { method: "POST", body: { token, password } }),
  changePassword: (current: string, next: string) =>
    api<{ ok: boolean }>("/api/auth/change-password", { method: "POST", body: { current, next } }),
  /**
   * ASSUMPTION (not yet in packages/shared/src/api.ts): PATCH /api/auth/me { name } → { user: Me }.
   * The profile form handles 404/405 gracefully until it exists.
   */
  updateProfile: (body: { name: string }) => api<{ user: Me }>("/api/auth/me", { method: "PATCH", body }),
  extToken: () => api<LoginRes>("/api/auth/ext-token", { method: "POST", noAuthRedirect: true }),
  redeemHandoff: (code: string) => api<{ user: Me }>("/api/auth/handoff/redeem", { method: "POST", body: { code }, noAuthRedirect: true }),
  impersonate: (userId: ID) => api<{ user: Me }>("/api/auth/impersonate", { method: "POST", body: { userId } }),
  stopImpersonating: () => api<{ user: Me }>("/api/auth/stop-impersonating", { method: "POST" }),
};

export interface IgAccountsOverviewRes {
  items: IgAccountOverview[];
  /** The plan's Instagram account limit. */
  limit: number;
  /** Accounts connected now. */
  used: number;
}

export const igAccountsApi = {
  list: () => api<{ items: IgAccount[] }>("/api/ig-accounts"),
  /** All-accounts view: each account + today's activity, contacts and automation switch. */
  overview: () => api<IgAccountsOverviewRes>("/api/ig-accounts/overview"),
  disconnect: (id: ID) => api<{ ok: boolean }>(`/api/ig-accounts/${encodeURIComponent(id)}`, { method: "DELETE" }),
  /** Clear which browser runs the account; the next browser that reports it takes over. May 404 on older APIs. */
  releaseDriver: (id: ID) => api<{ ok: boolean }>(`/api/ig-accounts/${encodeURIComponent(id)}/driver/release`, { method: "PUT", body: {} }),
};

/**
 * Settings are per Instagram account (workspaces). Pass the selected account's id; null reads/writes
 * the user-level defaults that newly connected accounts start from.
 */
export const settingsApi = {
  get: (igAccountId: ID | null) => api<SettingsMap>("/api/settings", { query: { igAccountId } }),
  put: <V>(key: string, value: V, igAccountId: ID | null) =>
    api<{ value: V }>(`/api/settings/${encodeURIComponent(key)}`, { method: "PUT", body: { value }, query: { igAccountId } }),
};

export interface ResetPasswordRes {
  ok: boolean;
  /** Set-password link (only when no password was set directly). */
  inviteLink?: string;
  /** Whether the email actually went out (false when mail isn't configured). */
  emailed?: boolean;
}

/** The API adds `emailed` to CreateAccountRes (not yet in packages/shared). */
export type CreateAccountResult = CreateAccountRes & { emailed?: boolean };

export type ResellerUsageAccountsRes = Omit<ResellerAccountsRes, "items"> & { items: UsageAccount[] };
export const resellerApi = {
  list: (q: { q?: string; cursor?: string | null; limit?: number }) =>
    api<ResellerUsageAccountsRes>("/api/reseller/accounts", { query: { q: q.q, cursor: q.cursor, limit: q.limit } }),
  create: (body: CreateAccountReq) => api<CreateAccountResult>("/api/reseller/accounts", { method: "POST", body }),
  update: (id: ID, body: UpdateAccountReq) => api<{ user: User }>(`/api/reseller/accounts/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  resetPassword: (id: ID, password?: string) =>
    api<ResetPasswordRes>(`/api/reseller/accounts/${encodeURIComponent(id)}/reset-password`, {
      method: "POST",
      body: password ? { password } : {},
    }),
  remove: (id: ID) => api<{ ok: boolean }>(`/api/reseller/accounts/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export type AdminUser = UsageAccount & { accountSource?: { parent_name: string | null; parent_email: string | null; parent_role: string | null; subscription_status: string | null; appsumo: boolean }; freeTrial?: {phone:string;signup_at:number;access_kind:string} | null; seatsUsed?: number; purchases?: { currency: string; amount: number; orders: number; source: "website" | "stripe" }[] | null };

/** GET /api/admin/users/:id (additive admin endpoint, not in packages/shared). */
export interface AdminAuditEntry {
  id: string;
  actorId: string | null;
  action: string;
  meta: Record<string, unknown> | null;
  createdAt: number;
}
export interface AdminUserDetail {
  user: AdminUser;
  audit: AdminAuditEntry[];
}

export const adminApi = {
  stats: () => api<AdminStats & { freeTrials:number; paidCustomers: number | null; payingResellers: number | null }>("/api/admin/stats"),
  users: (q: { instagram?: "connected" | "not_connected"; status?: "active" | "suspended"; q?: string; role?: Role | ""; purchased?: boolean; freeTrial?: boolean; parentId?: ID; cursor?: string | null; limit?: number }) =>
    api<Page<AdminUser>>("/api/admin/users", { query: { instagram: q.instagram, status: q.status, q: q.q, role: q.role, purchased: q.purchased, freeTrial:q.freeTrial, parentId: q.parentId, cursor: q.cursor, limit: q.limit } }),
  user: (id: ID) => api<AdminUserDetail>(`/api/admin/users/${encodeURIComponent(id)}`),
  create: (body: CreateAccountReq) => api<CreateAccountResult>("/api/admin/users", { method: "POST", body }),
  update: (id: ID, body: UpdateAccountReq) => api<{ user: User }>(`/api/admin/users/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  resetPassword: (id: ID, password?: string) =>
    api<ResetPasswordRes>(`/api/admin/users/${encodeURIComponent(id)}/reset-password`, {
      method: "POST",
      body: password ? { password } : {},
    }),
  /**
   * Deleting a reseller that still has customers fails with 409 `has_customers`;
   * `force` (?force=1) detaches them (they become direct customers) and deletes.
   */
  remove: (id: ID, opts: { force?: boolean } = {}) =>
    api<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(id)}`, { method: "DELETE", query: { force: opts.force ? 1 : undefined } }),
};

export const crmTasksApi = {
  list: (q: { igAccountId?: ID; status?: "open" | "done" | "all"; contactId?: ID }) =>
    api<{ items: TaskWithContact[] }>("/api/crm/tasks", { query: q }),
  update: (id: ID, body: { title?: string; dueAt?: number | null; done?: boolean }) =>
    api<Task>(`/api/crm/tasks/${encodeURIComponent(id)}`, { method: "PATCH", body }),
};
